(function () {
  const DISCLOSURE_SELECTOR = '[data-nola-disclosure]';
  const TRIGGER_SELECTOR = '[data-nola-disclosure-trigger]';
  const PANEL_SELECTOR = '[data-nola-disclosure-panel]';
  const REVEAL_SELECTOR = [
    '.nola-hero__content',
    '.nola-category-grid__item',
    '.nola-promo-banner__content',
    '.nola-product-card',
    '.nola-pdp__media',
    '.nola-pdp__details > :not(script):not(style)',
    '.nola-pdp-bundle',
    '.nola-pdp-ugc__item',
    '.nola-pdp-accordions__row',
    '.nola-pdp-look-grid',
    '.nola-pdp-story',
    '.nola-newsletter > *',
    '.nola-footer__inner > *'
  ].join(',');

  const motionReduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let id = 0;

  function nextId(prefix) {
    id += 1;
    return `${prefix}-${id}`;
  }

  function getPanelHeight(panel) {
    const previous = panel.style.blockSize;
    panel.style.blockSize = 'auto';
    const height = panel.scrollHeight;
    panel.style.blockSize = previous;
    return height;
  }

  function finishPanel(panel, open) {
    panel.dataset.nolaDisclosureAnimating = 'false';
    panel.style.blockSize = open ? 'auto' : '';
  }

  function setDisclosureState(disclosure, open, animate) {
    const trigger = disclosure.querySelector(TRIGGER_SELECTOR);
    const panel = disclosure.querySelector(PANEL_SELECTOR);

    if (!(trigger instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

    if (typeof panel.__nolaDisclosureCleanup === 'function') {
      panel.__nolaDisclosureCleanup();
      panel.__nolaDisclosureCleanup = null;
    }

    const shouldAnimate = animate && !motionReduced();
    const startHeight = panel.getBoundingClientRect().height;

    if (shouldAnimate) {
      panel.style.blockSize = `${startHeight}px`;
      panel.dataset.nolaDisclosureAnimating = 'true';
      panel.getBoundingClientRect();
    }

    trigger.setAttribute('aria-expanded', String(open));
    panel.setAttribute('aria-hidden', String(!open));
    disclosure.classList.toggle('is-open', open);

    if (open) {
      panel.removeAttribute('inert');
      panel.inert = false;
    } else {
      panel.setAttribute('inert', '');
      panel.inert = true;
    }

    if (!shouldAnimate) {
      finishPanel(panel, open);
      return;
    }

    const endHeight = open ? getPanelHeight(panel) : 0;

    let completed = false;

    const complete = () => {
      if (completed) return;
      completed = true;
      finishPanel(panel, open);
      panel.removeEventListener('transitionend', onEnd);
      window.clearTimeout(fallback);
      panel.__nolaDisclosureCleanup = null;
    };

    const onEnd = (event) => {
      if (event.target !== panel || !['block-size', 'height'].includes(event.propertyName)) return;
      complete();
    };

    const fallback = window.setTimeout(complete, 620);

    panel.__nolaDisclosureCleanup = () => {
      panel.removeEventListener('transitionend', onEnd);
      window.clearTimeout(fallback);
    };

    panel.addEventListener('transitionend', onEnd);

    requestAnimationFrame(() => {
      panel.style.blockSize = `${endHeight}px`;
    });
  }

  function initDisclosure(disclosure) {
    if (!(disclosure instanceof HTMLElement) || disclosure.dataset.nolaDisclosureReady === 'true') return;

    const trigger = disclosure.querySelector(TRIGGER_SELECTOR);
    const panel = disclosure.querySelector(PANEL_SELECTOR);

    if (!(trigger instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

    disclosure.dataset.nolaDisclosureReady = 'true';

    if (!panel.id) panel.id = nextId('nola-disclosure-panel');
    if (!trigger.id) trigger.id = `${panel.id}-trigger`;

    trigger.setAttribute('aria-controls', panel.id);
    panel.setAttribute('role', panel.getAttribute('role') || 'region');
    panel.setAttribute('aria-labelledby', trigger.id);

    const lockedOpen = disclosure.hasAttribute('data-nola-disclosure-locked-open');
    const open = lockedOpen || trigger.getAttribute('aria-expanded') === 'true' || disclosure.classList.contains('is-open');
    setDisclosureState(disclosure, open, false);

    trigger.addEventListener('click', () => {
      if (lockedOpen) {
        setDisclosureState(disclosure, true, false);
        return;
      }
      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      setDisclosureState(disclosure, !expanded, true);
    });
  }

  function initDisclosures(root) {
    const disclosures = root.matches?.(DISCLOSURE_SELECTOR)
      ? [root]
      : Array.from(root.querySelectorAll(DISCLOSURE_SELECTOR));

    disclosures.forEach(initDisclosure);
  }

  function getRevealSiblings(element) {
    const parent = element.parentElement;
    if (!parent) return [element];
    return Array.from(parent.children).filter((child) => child.matches?.(REVEAL_SELECTOR));
  }

  function initReveals(root) {
    if (motionReduced() || !('IntersectionObserver' in window)) return;

    const elements = root.matches?.(REVEAL_SELECTOR)
      ? [root]
      : Array.from(root.querySelectorAll(REVEAL_SELECTOR));

    elements.forEach((element) => {
      if (!(element instanceof HTMLElement) || element.dataset.nolaRevealReady === 'true') return;

      const siblings = getRevealSiblings(element);
      const index = Math.max(0, siblings.indexOf(element));
      const delay = Math.min(index * 55, 220);

      element.dataset.nolaReveal = '';
      element.dataset.nolaRevealReady = 'true';
      element.style.setProperty('--nola-reveal-delay', `${delay}ms`);
      revealObserver.observe(element);
    });

    document.documentElement.classList.add('nola-motion-ready');
  }

  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
      )
    : null;

  function init(root) {
    document.documentElement.classList.remove('no-js');
    document.documentElement.classList.add('js');
    initDisclosures(root);
    if (revealObserver) initReveals(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();
